import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Sidebar from '../Components/Sidebar/Sidebar';
import verificandoApp from '../../FuncoesGerais/verificaApp';
import verificandoToken from '../../FuncoesGerais/VerificarToken';
import { fetchFaturamento, reprocessarValor, minutosParaTexto } from '../../api/worklog';
import { fetchAnalistas } from '../../api/usuarios';
import './Worklog.css';

function iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function moeda(v) { return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }

function Faturamento() {
    const history = useHistory();
    const primeiroDia = new Date(); primeiroDia.setDate(1);

    const [de, setDe] = useState(iso(primeiroDia));
    const [ate, setAte] = useState(iso(new Date()));
    const [cliente, setCliente] = useState('');
    const [usuarioId, setUsuarioId] = useState('');
    const [analistas, setAnalistas] = useState([]);
    const [dados, setDados] = useState({ linhas: [], totais: { minutos: 0, minutos_faturaveis: 0, valor: 0 } });
    const [loading, setLoading] = useState(false);
    const [reprocessando, setReprocessando] = useState(false);
    const [aviso, setAviso] = useState('');

    const verificar = useCallback(async () => {
        const ok = await verificandoApp('ATENDENTE');
        if (!ok) history.push('/');
    }, [history]);

    const carregar = useCallback(async () => {
        verificandoToken();
        setLoading(true);
        try {
            const d = await fetchFaturamento({ de, ate, cliente: cliente || undefined, usuario_id: usuarioId || undefined });
            setDados(d || { linhas: [], totais: { minutos: 0, minutos_faturaveis: 0, valor: 0 } });
        } finally { setLoading(false); }
    }, [de, ate, cliente, usuarioId]);

    useEffect(() => { verificar(); fetchAnalistas().then((l) => setAnalistas(Array.isArray(l) ? l : [])); }, [verificar]);
    useEffect(() => { carregar(); }, [carregar]);

    const exportar = () => {
        const rows = (dados.linhas || []).map((l) => ({
            Cliente: l.cliente || '(sem cliente)',
            Colaborador: l.colaborador || '—',
            Chamado: l.sat_num,
            'Horas totais': (Number(l.minutos) / 60).toFixed(2),
            'Horas faturáveis': (Number(l.minutos_faturaveis) / 60).toFixed(2),
            'Valor (R$)': Number(l.valor || 0).toFixed(2),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Faturamento');
        XLSX.writeFile(wb, `faturamento_${de}_a_${ate}.xlsx`);
    };

    const reprocessar = async () => {
        if (!window.confirm('Recalcular o valor dos apontamentos faturáveis deste período com a tarifa atual?')) return;
        setReprocessando(true);
        setAviso('');
        try {
            const r = await reprocessarValor({ de, ate, cliente: cliente || undefined, usuario_id: usuarioId || undefined });
            setAviso(r?.message || 'Valores reprocessados.');
            await carregar();
        } catch (e) {
            setAviso(e?.response?.data?.error || 'Erro ao reprocessar valores.');
        } finally {
            setReprocessando(false);
        }
    };

    const linhas = dados.linhas || [];

    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <div className="container-fluid titulo cadastro-unificado wl-page">
                    <div className="page-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                            <h1 className="mb-0"><i className="fas fa-file-invoice-dollar me-2" />Faturamento</h1>
                            <p className="text-muted small mb-0">Horas faturáveis e valor por cliente, colaborador e chamado.</p>
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reprocessar} disabled={reprocessando}
                                title="Recalcula o valor dos apontamentos faturáveis com a tarifa atual">
                                <i className="fas fa-rotate me-1" />{reprocessando ? 'Reprocessando...' : 'Reprocessar valores'}
                            </button>
                            <button type="button" className="btn btn-success btn-sm" onClick={exportar} disabled={linhas.length === 0}>
                                <i className="fas fa-file-excel me-1" />Exportar Excel
                            </button>
                        </div>
                    </div>

                    {aviso && <div className="alert alert-info py-2 my-2">{aviso}</div>}

                    <div className="wl-filtros d-flex flex-wrap align-items-end gap-2 my-3">
                        <div><label className="small d-block">De</label><input type="date" className="form-control form-control-sm" value={de} onChange={(e) => setDe(e.target.value)} /></div>
                        <div><label className="small d-block">Até</label><input type="date" className="form-control form-control-sm" value={ate} onChange={(e) => setAte(e.target.value)} /></div>
                        <div><label className="small d-block">Cliente</label><input className="form-control form-control-sm" value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Todos" style={{ minWidth: 180 }} /></div>
                        <div>
                            <label className="small d-block">Colaborador</label>
                            <select className="form-control form-control-sm" value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} style={{ minWidth: 200 }}>
                                <option value="">Todos</option>
                                {analistas.map((a) => <option key={a.USUSEQ ?? a.ususeq} value={a.USUSEQ ?? a.ususeq}>{a.USRNOMCPL ?? a.usrnomcpl ?? ('Usuário ' + (a.USUSEQ ?? a.ususeq))}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="wl-totais d-flex flex-wrap gap-3 mb-3">
                        <div className="wl-metric"><div className="wl-metric-label">Horas totais</div><div className="wl-metric-valor">{minutosParaTexto(dados.totais.minutos)}</div></div>
                        <div className="wl-metric"><div className="wl-metric-label">Horas faturáveis</div><div className="wl-metric-valor">{minutosParaTexto(dados.totais.minutos_faturaveis)}</div></div>
                        <div className="wl-metric wl-metric-destaque"><div className="wl-metric-label">Valor a faturar</div><div className="wl-metric-valor">{moeda(dados.totais.valor)}</div></div>
                    </div>

                    {loading && <p className="text-muted">Carregando...</p>}
                    {!loading && linhas.length === 0 && <div className="alert alert-info py-3">Nenhuma hora no período/filtro.</div>}

                    {!loading && linhas.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-sm wl-table">
                                <thead>
                                    <tr>
                                        <th>Cliente</th><th>Colaborador</th><th>Chamado</th>
                                        <th className="text-end">Horas</th><th className="text-end">Faturáveis</th><th className="text-end">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhas.map((l, i) => (
                                        <tr key={i}>
                                            <td>{l.cliente || <span className="text-muted">(sem cliente)</span>}</td>
                                            <td>{l.colaborador || '—'}</td>
                                            <td><button className="btn btn-link btn-sm p-0" onClick={() => history.push(`/historicoSati/${l.sat_num}`)}>#{l.sat_num}</button></td>
                                            <td className="text-end text-nowrap">{minutosParaTexto(l.minutos)}</td>
                                            <td className="text-end text-nowrap">{minutosParaTexto(l.minutos_faturaveis)}</td>
                                            <td className="text-end text-nowrap">{moeda(l.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="wl-foot">
                                        <td colSpan={3} className="text-end"><strong>Total</strong></td>
                                        <td className="text-end">{minutosParaTexto(dados.totais.minutos)}</td>
                                        <td className="text-end">{minutosParaTexto(dados.totais.minutos_faturaveis)}</td>
                                        <td className="text-end"><strong>{moeda(dados.totais.valor)}</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default Faturamento;
